import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { getEventTypeConfig, formatEventDateTime } from '../utils/eventTypes';
import { getOpenAssignments } from '../utils/eventUtils';
import OpenAssignmentDisplay from '../details/OpenAssignmentDisplay';
import EventHeader from '../../../components/ui/events/EventHeader';
import EventDetails from '../details/EventDetails';
import EventNotesSection from '../details/EventNotesSection';
import EventImageModal from '../details/EventImageModal';
import { useEventNotes } from '../../../hooks/useEventNotes';
import { useEventSpeech } from '../../../hooks/useEventSpeech';
import { useCapturedImage } from '../../../hooks/useCapturedImage';

/**
 * EventActionDialog - Shows event details with edit and delete options
 * @param {boolean} open - Whether the dialog is open
 * @param {Object} event - Event to show actions for
 * @param {Array} allEvents - All events (to find open assignments)
 * @param {Function} onClose - Callback when dialog is closed
 * @param {Function} onEdit - Callback when edit is clicked
 * @param {Function} onDelete - Callback when delete is clicked
 */
export default function EventActionDialog({
  open,
  event,
  allEvents = [],
  onClose,
  onEdit,
  onDelete
}) {
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('success');
  const [imageModalOpen, setImageModalOpen] = React.useState(false);
  const [imageModalSrc, setImageModalSrc] = React.useState('');

  // Snackbar helper
  const showSnackbar = React.useCallback((message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Custom hooks for managing complex state
  const {
    noteText,
    setNoteText,
    saving,
    editingNoteIndex,
    existingNotes,
    handleEditNote,
    handleCancelEdit,
    handleDeleteNote,
    handleSaveNote
  } = useEventNotes(event, onClose, showSnackbar);

  const {
    isListening,
    setIsNoteFocused,
    handleStopListening
  } = useEventSpeech(noteText, setNoteText, showSnackbar, open);

  const { capturedImageSignedUrl, loadingCapturedImage } = useCapturedImage(
    open,
    event?.metadata?.capturedImageUrl
  );

  if (!event) return null;

  const config = getEventTypeConfig(event.eventType);
  const dateTimeStr = formatEventDateTime(event.createdAt);
  const isMatchEvent = event.eventType === 'match';
  const isGroupMatchEvent = event.eventType === 'group_match';
  const isNoteEvent = event.eventType === 'note';
  const personImage = event.metadata?.personImage;
  const personName = event.metadata?.personName;
  const matchSource = event.metadata?.matchSource;
  const systemNote = event.metadata?.note;
  const openAssignments = getOpenAssignments(event, allEvents);

  // Handler to toggle between captured and reference images
  const handleToggleImage = () => {
    setImageModalSrc(prev =>
      prev === capturedImageSignedUrl ? personImage : capturedImageSignedUrl
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Event Details</DialogTitle>
      <DialogContent>
        <Box>
          {isMatchEvent && (
            <EventHeader
              personImage={personImage}
              personName={personName}
              matchSource={matchSource}
              capturedImageSignedUrl={capturedImageSignedUrl}
              capturedImageUrl={event.metadata?.capturedImageUrl}
              loadingCapturedImage={loadingCapturedImage}
              matchAccuracy={event.metadata?.matchAccuracy}
              eventLabel={config.label}
              personUri={event.metadata?.person?.uri}
              matchCount={event.metadata?.matchCount}
              detectedFaceCount={event.metadata?.detectedFaceCount}
              boundingBox={event.metadata?.boundingBox}
              onPersonImageClick={() => {
                setImageModalSrc(personImage);
                setImageModalOpen(true);
              }}
              onCapturedImageClick={() => {
                if (capturedImageSignedUrl) {
                  setImageModalSrc(capturedImageSignedUrl);
                  setImageModalOpen(true);
                }
              }}
            />
          )}

          {isGroupMatchEvent && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={capturedImageSignedUrl || ''}
                alt="Group photo"
                onClick={() => {
                  setImageModalSrc(capturedImageSignedUrl);
                  setImageModalOpen(true);
                }}
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 1,
                  objectFit: 'cover',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              />
              <Box>
                <Typography variant="h6">
                  {event.metadata?.matchCount}/{event.metadata?.detectedFaceCount} faces matched
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {event.metadata?.personNames?.join(', ')}
                </Typography>
              </Box>
            </Box>
          )}

          <EventDetails
            isMatchEvent={isMatchEvent}
            isNoteEvent={isNoteEvent}
            eventLabel={config.label}
            description={event.description}
            dateTimeStr={dateTimeStr}
            matchSource={matchSource}
            systemNote={systemNote}
            location={event.location}
          />

          {isMatchEvent && <OpenAssignmentDisplay openAssignments={openAssignments} />}

          {(isMatchEvent || isNoteEvent) && (
            <EventNotesSection
              noteText={noteText}
              onNoteTextChange={setNoteText}
              onSave={handleSaveNote}
              onCancel={handleCancelEdit}
              saving={saving}
              isEditing={editingNoteIndex !== null}
              isListening={isListening}
              onStopListening={handleStopListening}
              onFocus={() => setIsNoteFocused(true)}
              onBlur={() => setIsNoteFocused(false)}
              notes={existingNotes}
              editingNoteIndex={editingNoteIndex}
              onEditNote={handleEditNote}
              onDeleteNote={handleDeleteNote}
              borderColor={isNoteEvent ? 'rgba(255, 152, 0, 0.5)' : undefined}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!isNoteEvent && (
          <Button onClick={onEdit} variant="outlined">Edit</Button>
        )}
        <Button onClick={onDelete} variant="outlined" color="error">
          Delete
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <EventImageModal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        imageSrc={imageModalSrc}
        capturedImageUrl={capturedImageSignedUrl}
        personImage={personImage}
        onToggleImage={handleToggleImage}
      />
    </Dialog>
  );
}
